import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  Edge,
  Node,
  NodeTypes,
  ReactFlow,
  ReactFlowInstance,
  ReactFlowProvider,
} from '@xyflow/react';
import styled from 'styled-components';
import { AlertTriangle, LocateFixed, RefreshCw, Undo2 } from 'lucide-react';
import '@xyflow/react/dist/style.css';
import {
  createOrganizationTreeNode,
  deleteOrganizationTreeNode,
  fetchServiceRoutingOptions,
  fetchOrganizationTreeAudit,
  fetchOrganizationTree,
  fetchOrganizationOperators,
  updateOrganizationTreeNode,
} from './api';
import type { OrganizationTreeAuditResponse, ServiceRoutingOption } from './api';
import {
  buildTreeLayout,
  getAncestorEdgeSet,
  getAncestorSet,
  LayoutDirection,
  SKILLS_NODE_HEIGHT,
  SKILLS_NODE_WIDTH,
} from './layout';
import {
  SkillsNodeData,
  SkillsNodeType,
  SkillsTreeAuditAction,
  SkillsTreeAuditEntry,
  SkillsTreeNode,
  SkillsTreeNodeMeta,
  SkillsTreeOperator,
} from './types';
import { NodeCard } from './components/NodeCard';
import { NodeDetailsDrawer } from './components/NodeDetailsDrawer';
import { useAuth } from '../../context/AuthProvider';

const nodeTypes: NodeTypes = {
  skillNode: NodeCard,
};

type SkillsFlowNode = Node<SkillsNodeData, 'skillNode'>;

const NODE_CHILD_TYPE_MAP: Record<SkillsNodeType, SkillsNodeType | null> = {
  org: 'client',
  client: 'service_line',
  service_line: 'project',
  project: 'deliverable',
  deliverable: null,
};

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
  height: calc(100dvh - 2.5rem);
  min-height: calc(100dvh - 2.5rem);

  @media (max-width: 767px) {
    height: calc(100dvh - 5rem);
    min-height: calc(100dvh - 5rem);
  }
`;

const CanvasShell = styled.section`
  position: relative;
  flex: 1;
  min-height: 0;
  height: 100%;
  border-radius: 16px;
  background: transparent;
  overflow: hidden;
`;

const CanvasHeader = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 6;
  max-width: min(660px, calc(100% - 24px));
  border-radius: 12px;
  // background: linear-gradient(
  //   100deg,
  //   rgba(8, 12, 30, 0.9) 0%,
  //   rgba(8, 12, 30, 0.72) 70%,
  //   rgba(8, 12, 30, 0.18) 100%
  // );
  // border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 10px 12px;
  pointer-events: none;
  display: grid;
  gap: 6px;
`;

const CanvasHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const CanvasTitle = styled.h1`
  margin: 0;
  color: #e0a15e;
  font-size: clamp(1.1rem, 2.2vw, 1.65rem);
  line-height: 1.05;
  font-weight: 900;
`;

const CanvasSubtitle = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.78rem;
  font-weight: 700;
`;

const SyncBadge = styled.span<{ $tone: 'ready' | 'busy' | 'error' }>`
  border-radius: 999px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'error'
        ? 'rgba(255, 122, 122, 0.6)'
        : $tone === 'busy'
          ? 'rgba(224, 161, 94, 0.6)'
          : 'rgba(120, 219, 150, 0.52)'};
  background: ${({ $tone }) =>
    $tone === 'error'
      ? 'rgba(166, 76, 68, 0.35)'
      : $tone === 'busy'
        ? 'rgba(224, 161, 94, 0.18)'
        : 'rgba(88, 186, 117, 0.2)'};
  color: ${({ $tone }) =>
    $tone === 'error'
      ? 'rgba(255, 191, 191, 0.95)'
      : $tone === 'busy'
        ? 'rgba(248, 218, 179, 0.95)'
        : 'rgba(196, 250, 212, 0.95)'};
  padding: 0.2rem 0.54rem;
  font-size: 0.65rem;
  font-weight: 900;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const ErrorBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  border-radius: 10px;
  border: 1px solid rgba(255, 122, 122, 0.5);
  background: rgba(166, 76, 68, 0.28);
  color: rgba(255, 217, 217, 0.95);
  padding: 0.34rem 0.52rem;
  font-size: 0.7rem;
  font-weight: 800;
  pointer-events: auto;
`;

const AuditBanner = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  width: fit-content;
  max-width: min(640px, calc(100vw - 36px));
  border-radius: 10px;
  border: 1px solid rgba(255, 191, 122, 0.5);
  background: rgba(120, 72, 24, 0.28);
  color: rgba(255, 235, 209, 0.96);
  padding: 0.42rem 0.58rem;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1.4;
  pointer-events: auto;
`;

const RetryButton = styled.button`
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.32);
  background: transparent;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.67rem;
  font-weight: 900;
  padding: 0.18rem 0.46rem;
  cursor: pointer;
`;

const ControlRail = styled.div`
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  z-index: 6;
  display: grid;
  gap: 8px;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(8, 12, 30, 0.84);
`;

const ControlButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.32);
  background: transparent;
  color: rgba(238, 241, 248, 0.95);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  pointer-events: none;
`;

const EmptyPill = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(8, 12, 30, 0.82);
  color: rgba(242, 242, 242, 0.88);
  padding: 0.5rem 0.72rem;
  font-size: 0.82rem;
  font-weight: 800;
`;

const BranchModalBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: rgba(3, 5, 16, 0.62);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 160ms ease;
  z-index: 156;
`;

const BranchModal = styled.section<{ $open: boolean }>`
  position: fixed;
  left: 50%;
  top: 50%;
  width: min(460px, calc(100vw - 2rem));
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: linear-gradient(145deg, rgba(10, 14, 34, 0.96) 0%, rgba(8, 12, 30, 0.94) 100%);
  box-shadow: 0 22px 48px rgba(0, 0, 0, 0.48);
  padding: 14px;
  display: grid;
  gap: 10px;
  z-index: 157;
  transform: translate(-50%, -50%) scale(${({ $open }) => ($open ? 1 : 0.96)});
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition:
    transform 160ms ease,
    opacity 160ms ease;
`;

const BranchModalTitle = styled.h3`
  margin: 0;
  color: #e0a15e;
  font-size: 1.06rem;
  line-height: 1.2;
  font-weight: 900;
`;

const BranchModalSub = styled.p`
  margin: 0;
  color: rgba(231, 236, 246, 0.82);
  font-size: 0.82rem;
  font-weight: 700;
`;

const BranchField = styled.label`
  display: grid;
  gap: 5px;
  color: rgba(232, 236, 244, 0.82);
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 900;
`;

const BranchInput = styled.input`
  min-height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.34);
  background: transparent;
  color: rgba(246, 248, 251, 0.95);
  padding: 0 10px;
  font-size: 0.88rem;
  font-weight: 800;
`;

const BranchSelect = styled.select`
  min-height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.34);
  background: rgba(8, 12, 30, 0.92);
  color: rgba(246, 248, 251, 0.95);
  padding: 0 10px;
  font-size: 0.88rem;
  font-weight: 800;
`;

const BranchActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const BranchActionButton = styled.button<{ $tone: 'neutral' | 'primary' }>`
  min-height: 36px;
  border-radius: 999px;
  border: ${({ $tone }) => ($tone === 'neutral' ? '1px solid rgba(255, 255, 255, 0.34)' : 'none')};
  background: ${({ $tone }) => ($tone === 'neutral' ? 'transparent' : '#e0a15e')};
  color: ${({ $tone }) => ($tone === 'neutral' ? 'rgba(242, 242, 242, 0.95)' : 'rgba(14, 14, 14, 0.95)')};
  padding: 0 14px;
  font-size: 0.78rem;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DeleteModal = styled(BranchModal)``;

const DeleteModalTitle = styled(BranchModalTitle)`
  color: rgba(255, 148, 148, 0.95);
`;

const DeleteModalMessage = styled.p`
  margin: 0;
  color: rgba(235, 238, 245, 0.88);
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.35;
`;

const DeleteActionButton = styled(BranchActionButton)<{ $tone: 'neutral' | 'danger' }>`
  background: ${({ $tone }) => ($tone === 'danger' ? 'rgba(166, 76, 68, 0.9)' : 'transparent')};
  color: ${({ $tone }) =>
    $tone === 'danger' ? 'rgba(255, 233, 233, 0.98)' : 'rgba(242, 242, 242, 0.95)'};
  border: ${({ $tone }) =>
    $tone === 'danger' ? 'none' : '1px solid rgba(255, 255, 255, 0.34)'};
`;

const StartDirection: LayoutDirection = 'TB';
const MAX_AUDIT_ENTRIES = 120;

type LastMoveSnapshot = {
  nodeId: string;
  fromParentId: string;
  toParentId: string;
};

type BranchDraft = {
  parentNodeId: string;
  parentTitle: string;
  childType: SkillsNodeType;
  designationOptions: string[];
  selectedDesignation: string;
  customDesignation: string;
};

type EditableNodeMeta = {
  position: string;
  team: string;
  designation: string;
  childDesignation: string;
  appointedAgent: string;
  appointedAgentUid: string;
  appointedAgentEmail: string;
  appointedAgentAvatarUrl: string;
};

type DeleteDraft = {
  nodeId: string;
  title: string;
  descendants: number;
};

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Tree sync failed.';
}

function sanitizeAuditTrail(meta: SkillsTreeNodeMeta | null | undefined): SkillsTreeAuditEntry[] {
  if (!meta || typeof meta !== 'object') return [];
  const raw = meta.auditTrail;
  if (!Array.isArray(raw)) return [];
  const normalized: SkillsTreeAuditEntry[] = [];

  for (let index = 0; index < raw.length; index += 1) {
    const entry = raw[index];
    if (!entry || typeof entry !== 'object') continue;
    const candidate = entry as Partial<SkillsTreeAuditEntry>;
    if (
      typeof candidate.id !== 'string' ||
      typeof candidate.at !== 'string' ||
      typeof candidate.actorId !== 'string' ||
      typeof candidate.actorLabel !== 'string' ||
      typeof candidate.action !== 'string' ||
      typeof candidate.summary !== 'string'
    ) {
      continue;
    }
    normalized.push({
      id: candidate.id,
      at: candidate.at,
      actorId: candidate.actorId,
      actorLabel: candidate.actorLabel,
      action: candidate.action as SkillsTreeAuditAction,
      summary: candidate.summary,
    });
  }

  return normalized.slice(0, MAX_AUDIT_ENTRIES);
}

function randomAuditId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function withAuditEntry(
  meta: SkillsTreeNodeMeta | null | undefined,
  entry: SkillsTreeAuditEntry,
): SkillsTreeNodeMeta {
  const baseMeta = meta && typeof meta === 'object' ? meta : {};
  const existing = sanitizeAuditTrail(baseMeta);
  return {
    ...baseMeta,
    auditTrail: [entry, ...existing].slice(0, MAX_AUDIT_ENTRIES),
  };
}

function nodeLocked(node: SkillsTreeNode | null | undefined): boolean {
  if (!node || !node.meta || typeof node.meta !== 'object') return false;
  return Boolean(node.meta.locked);
}

function normalizeDesignation(value: string): string {
  const cleaned = value.replace(/[^a-zA-Z0-9\s]/g, ' ');
  const normalized = cleaned.replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  return normalized
    .split(' ')
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(' ');
}

function singularizeDesignation(value: string): string {
  const normalized = normalizeDesignation(value);
  if (!normalized) return '';
  if (/ies$/i.test(normalized)) return normalized.replace(/ies$/i, 'y');
  if (/s$/i.test(normalized) && !/ss$/i.test(normalized)) return normalized.slice(0, -1);
  return normalized;
}

function parseDesignationOptions(raw: unknown): string[] {
  if (typeof raw !== 'string') return [];
  const unique = new Set<string>();
  raw
    .split(/[\n,;|]+/g)
    .map((value) => singularizeDesignation(value.trim()))
    .filter(Boolean)
    .forEach((value) => unique.add(value));
  return [...unique];
}

function isLegacyPlaceholderTitle(title: string): boolean {
  const normalized = title.trim().toLowerCase();
  if (!normalized) return true;
  return /^client\s+\d+$/i.test(normalized) || /^service line\s+\d+$/i.test(normalized);
}

function SkillsTreeCanvas() {
  const { user, role } = useAuth();
  const [treeNodes, setTreeNodes] = useState<SkillsTreeNode[]>([]);
  const [operatorOptions, setOperatorOptions] = useState<SkillsTreeOperator[]>([]);
  const [serviceRoutingOptions, setServiceRoutingOptions] = useState<ServiceRoutingOption[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [pendingFocusNodeId, setPendingFocusNodeId] = useState<string | null>(null);
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [dropTargetNodeId, setDropTargetNodeId] = useState<string | null>(null);
  const [lastMoveSnapshot, setLastMoveSnapshot] = useState<LastMoveSnapshot | null>(null);
  const [direction] = useState<LayoutDirection>(StartDirection);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOperators, setIsLoadingOperators] = useState(true);
  const [isLoadingRoutingOptions, setIsLoadingRoutingOptions] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [treeAudit, setTreeAudit] = useState<OrganizationTreeAuditResponse | null>(null);
  const [branchDraft, setBranchDraft] = useState<BranchDraft | null>(null);
  const [deleteDraft, setDeleteDraft] = useState<DeleteDraft | null>(null);
  const flowRef = useRef<ReactFlowInstance<SkillsFlowNode, Edge> | null>(null);

  const actorId = user?.uid ?? 'system';
  const actorLabel =
    user?.displayName?.trim() || user?.email?.trim() || 'System';
  const viewerUid = user?.uid ?? '';
  const hasGlobalEdit = role === 'admin';

  const { nodes: baseNodes, edges: baseEdges, parentMap } = useMemo(
    () => buildTreeLayout(treeNodes, { direction }),
    [treeNodes, direction],
  );

  const createAuditEntry = useCallback(
    (action: SkillsTreeAuditAction, summary: string): SkillsTreeAuditEntry => ({
      id: randomAuditId(),
      at: new Date().toISOString(),
      actorId,
      actorLabel,
      action,
      summary,
    }),
    [actorId, actorLabel],
  );

  const ancestorSet = useMemo(
    () => getAncestorSet(selectedNodeId, parentMap),
    [selectedNodeId, parentMap],
  );
  const ancestorEdgeSet = useMemo(
    () => getAncestorEdgeSet(selectedNodeId, parentMap),
    [selectedNodeId, parentMap],
  );

  const invalidRoutingPreview = useMemo(() => {
    if (!treeAudit?.invalidRoutedNodes?.length) return '';
    return treeAudit.invalidRoutedNodes
      .slice(0, 3)
      .map((entry) => entry.title.trim() || entry.nodeId)
      .filter(Boolean)
      .join(', ');
  }, [treeAudit]);

  const assignedRootNodeIds = useMemo(() => {
    if (!viewerUid) return [] as string[];
    const ids: string[] = [];
    for (let index = 0; index < treeNodes.length; index += 1) {
      const node = treeNodes[index];
      const meta = node.meta && typeof node.meta === 'object' ? (node.meta as Record<string, unknown>) : {};
      if (typeof meta.appointedAgentUid === 'string' && meta.appointedAgentUid.trim() === viewerUid) {
        ids.push(node.id);
      }
    }
    return ids;
  }, [treeNodes, viewerUid]);

  const isDescendantOfNode = useCallback(
    (candidateDescendantId: string, ancestorId: string) => {
      let cursor = parentMap.get(candidateDescendantId) ?? null;
      while (cursor) {
        if (cursor === ancestorId) return true;
        cursor = parentMap.get(cursor) ?? null;
      }
      return false;
    },
    [parentMap],
  );

  const canManageNodeId = useCallback(
    (nodeId: string) => {
      if (hasGlobalEdit) return true;
      if (!viewerUid) return false;
      if (!assignedRootNodeIds.length) return false;
      for (let index = 0; index < assignedRootNodeIds.length; index += 1) {
        const rootId = assignedRootNodeIds[index];
        if (nodeId === rootId || isDescendantOfNode(nodeId, rootId)) {
          return true;
        }
      }
      return false;
    },
    [assignedRootNodeIds, hasGlobalEdit, isDescendantOfNode, viewerUid],
  );

  const isValidParentForNode = useCallback(
    (draggedNodeId: string, parentNodeId: string) => {
      const dragged = treeNodes.find((node) => node.id === draggedNodeId);
      const parent = treeNodes.find((node) => node.id === parentNodeId);
      if (!dragged || !parent) return false;
      if (!canManageNodeId(dragged.id) || !canManageNodeId(parent.id)) return false;
      if (dragged.parentId === null) return false; // root node cannot be moved
      if (nodeLocked(dragged)) return false;
      if (nodeLocked(parent)) return false;
      if (dragged.id === parent.id) return false;
      if (isDescendantOfNode(parent.id, dragged.id)) return false; // cycle guard
      const expectedChildType = NODE_CHILD_TYPE_MAP[parent.type];
      // Allow same-level re-parenting (e.g., sibling -> sibling as new parent)
      // while preserving existing hierarchy compatibility.
      return expectedChildType === dragged.type || parent.type === dragged.type;
    },
    [canManageNodeId, isDescendantOfNode, treeNodes],
  );

  const detectDropTargetId = useCallback(
    (dragNode: { id: string; position: { x: number; y: number }; width?: number; height?: number }) => {
      const dragWidth = dragNode.width ?? SKILLS_NODE_WIDTH;
      const dragHeight = dragNode.height ?? SKILLS_NODE_HEIGHT;
      const dragCenterX = dragNode.position.x + dragWidth / 2;
      const dragCenterY = dragNode.position.y + dragHeight / 2;

      let nearest: { id: string; distance: number } | null = null;

      for (let index = 0; index < baseNodes.length; index += 1) {
        const candidate = baseNodes[index];
        if (candidate.id === dragNode.id) continue;
        if (!isValidParentForNode(dragNode.id, candidate.id)) continue;

        const candidateWidth = candidate.width ?? SKILLS_NODE_WIDTH;
        const candidateHeight = candidate.height ?? SKILLS_NODE_HEIGHT;
        const withinX =
          dragCenterX >= candidate.position.x && dragCenterX <= candidate.position.x + candidateWidth;
        const withinY =
          dragCenterY >= candidate.position.y && dragCenterY <= candidate.position.y + candidateHeight;

        if (!withinX || !withinY) continue;

        const candidateCenterX = candidate.position.x + candidateWidth / 2;
        const candidateCenterY = candidate.position.y + candidateHeight / 2;
        const distance = Math.hypot(dragCenterX - candidateCenterX, dragCenterY - candidateCenterY);

        if (!nearest || distance < nearest.distance) {
          nearest = { id: candidate.id, distance };
        }
      }

      return nearest?.id ?? null;
    },
    [baseNodes, isValidParentForNode],
  );

  const loadTree = useCallback(async () => {
    setIsLoading(true);
    setSyncError(null);

    try {
      const response = await fetchOrganizationTree(orgId ?? undefined);
      try {
        const auditResponse = await fetchOrganizationTreeAudit(response.orgId);
        setTreeAudit(auditResponse);
      } catch (auditError) {
        console.error('Failed to audit organization tree routing.', auditError);
        setTreeAudit(null);
      }
      setOrgId(response.orgId);
      setTreeNodes(response.nodes);
      setLastMoveSnapshot(null);
      setSelectedNodeId((previous) =>
        previous && response.nodes.some((node) => node.id === previous) ? previous : null,
      );
    } catch (error) {
      setSyncError(toErrorMessage(error));
      setTreeAudit(null);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void loadTree();
  }, [loadTree]);

  const loadOperators = useCallback(async () => {
    setIsLoadingOperators(true);
    try {
      const operators = await fetchOrganizationOperators();
      setOperatorOptions(operators);
    } catch (error) {
      setSyncError(toErrorMessage(error));
    } finally {
      setIsLoadingOperators(false);
    }
  }, []);

  useEffect(() => {
    void loadOperators();
  }, [loadOperators]);

  const loadRoutingOptions = useCallback(async () => {
    setIsLoadingRoutingOptions(true);
    try {
      const options = await fetchServiceRoutingOptions();
      setServiceRoutingOptions(options);
    } catch (error) {
      setSyncError(toErrorMessage(error));
    } finally {
      setIsLoadingRoutingOptions(false);
    }
  }, []);

  useEffect(() => {
    void loadRoutingOptions();
  }, [loadRoutingOptions]);

  const applyUpsertNode = useCallback((node: SkillsTreeNode) => {
    setTreeNodes((previous) => {
      const existingIndex = previous.findIndex((item) => item.id === node.id);
      if (existingIndex === -1) return [...previous, node];
      const next = [...previous];
      next[existingIndex] = node;
      return next;
    });
  }, []);

  const countNodeBranchSize = useCallback(
    (nodeId: string) => {
      const branchIds = new Set<string>([nodeId]);
      let changed = true;
      while (changed) {
        changed = false;
        for (let index = 0; index < treeNodes.length; index += 1) {
          const node = treeNodes[index];
          if (!node.parentId) continue;
          if (branchIds.has(node.parentId) && !branchIds.has(node.id)) {
            branchIds.add(node.id);
            changed = true;
          }
        }
      }
      return branchIds.size;
    },
    [treeNodes],
  );

  const openDeleteModal = useCallback(
    (nodeId: string) => {
      const target = treeNodes.find((node) => node.id === nodeId);
      if (!target || target.parentId === null) return;
      if (!canManageNodeId(target.id)) {
        setSyncError('You can only modify your assigned node and its child nodes.');
        return;
      }
      if (nodeLocked(target)) return;
      const branchSize = countNodeBranchSize(nodeId);
      const descendants = Math.max(0, branchSize - 1);
      setDeleteDraft({
        nodeId,
        title: target.title,
        descendants,
      });
    },
    [canManageNodeId, countNodeBranchSize, treeNodes],
  );

  const closeDeleteModal = useCallback(() => {
    if (isMutating) return;
    setDeleteDraft(null);
  }, [isMutating]);

  const openBranchModal = useCallback(
    (parentNodeId: string) => {
      const parentNode = treeNodes.find((node) => node.id === parentNodeId);
      if (!parentNode) return;
      if (!canManageNodeId(parentNode.id)) {
        setSyncError('You can only modify your assigned node and its child nodes.');
        return;
      }
      if (nodeLocked(parentNode)) return;

      const childType = NODE_CHILD_TYPE_MAP[parentNode.type];
      if (!childType) return;

      const options = parseDesignationOptions(parentNode.meta?.childDesignation);
      setBranchDraft({
        parentNodeId,
        parentTitle: parentNode.title,
        childType,
        designationOptions: options,
        selectedDesignation: options.length === 1 ? options[0] : '',
        customDesignation: '',
      });
    },
    [canManageNodeId, treeNodes],
  );

  const closeBranchModal = useCallback(() => {
    if (isMutating) return;
    setBranchDraft(null);
  }, [isMutating]);

  const createChildFromBranchDraft = useCallback(async () => {
    if (!branchDraft) return;
    const parentNode = treeNodes.find((node) => node.id === branchDraft.parentNodeId);
    if (!parentNode) {
      setBranchDraft(null);
      return;
    }
    if (!canManageNodeId(parentNode.id)) {
      setSyncError('You can only modify your assigned node and its child nodes.');
      return;
    }
    if (nodeLocked(parentNode)) return;

    const selected = singularizeDesignation(
      branchDraft.designationOptions.length
        ? branchDraft.selectedDesignation
        : branchDraft.customDesignation,
    );
    if (!selected) {
      setSyncError('Designation is required to create a child node.');
      return;
    }

    const defaultMeta = {
      position: '',
      team: '',
      designation: selected,
      childDesignation: '',
      appointedAgent: '',
      appointedAgentUid: '',
      appointedAgentEmail: '',
      appointedAgentAvatarUrl: '',
    };
    const auditedMeta = withAuditEntry(
      defaultMeta,
      createAuditEntry('created', `Node created under ${parentNode.title}.`),
    );

    setIsMutating(true);
    setSyncError(null);

    try {
      const response = await createOrganizationTreeNode({
        orgId: orgId ?? undefined,
        parentId: branchDraft.parentNodeId,
        type: branchDraft.childType,
        title: selected,
        status: 'active',
        meta: auditedMeta,
        rules: {},
      });

      if (!orgId && response.orgId) {
        setOrgId(response.orgId);
      }

      applyUpsertNode(response.node);
      setSelectedNodeId(response.node.id);
      setPendingFocusNodeId(response.node.id);
      setBranchDraft(null);
    } catch (error) {
      setSyncError(toErrorMessage(error));
    } finally {
      setIsMutating(false);
    }
  }, [applyUpsertNode, branchDraft, canManageNodeId, createAuditEntry, orgId, treeNodes]);

  const deleteBranch = useCallback(
    async (nodeId: string) => {
      const target = treeNodes.find((node) => node.id === nodeId);
      if (!target || target.parentId === null) return;
      if (!canManageNodeId(target.id)) {
        setSyncError('You can only modify your assigned node and its child nodes.');
        return;
      }
      if (nodeLocked(target)) return;

      setIsMutating(true);
      setSyncError(null);

      try {
        const response = await deleteOrganizationTreeNode(nodeId, orgId ?? undefined);
        const deletedSet = new Set(response.deletedNodeIds ?? []);

        setTreeNodes((previous) => previous.filter((node) => !deletedSet.has(node.id)));

        if (selectedNodeId && deletedSet.has(selectedNodeId)) {
          setSelectedNodeId(null);
        }

        if (pendingFocusNodeId && deletedSet.has(pendingFocusNodeId)) {
          setPendingFocusNodeId(null);
        }
        setDeleteDraft((previous) => (previous && previous.nodeId === nodeId ? null : previous));
      } catch (error) {
        setSyncError(toErrorMessage(error));
      } finally {
        setIsMutating(false);
      }
    },
    [
      canManageNodeId,
      orgId,
      pendingFocusNodeId,
      selectedNodeId,
      treeNodes,
    ],
  );

  const moveNodeToParent = useCallback(
    async (
      nodeId: string,
      parentId: string,
      options?: {
        trackForUndo?: boolean;
      },
    ) => {
      const movingNode = treeNodes.find((node) => node.id === nodeId);
      const parentNode = treeNodes.find((node) => node.id === parentId);
      if (!movingNode || !parentNode) return;
      if (!canManageNodeId(movingNode.id) || !canManageNodeId(parentNode.id)) {
        setSyncError('You can only modify your assigned node and its child nodes.');
        return;
      }
      if (nodeLocked(movingNode) || nodeLocked(parentNode)) return;
      if (movingNode.parentId === null) return;
      if (movingNode.parentId === parentId) return;
      if (!isValidParentForNode(nodeId, parentId)) return;

      const previousParentId = movingNode.parentId;
      const movedMeta = withAuditEntry(
        movingNode.meta,
        createAuditEntry('moved', `Moved under ${parentNode.title}.`),
      );

      setIsMutating(true);
      setSyncError(null);
      setTreeNodes((previous) =>
        previous.map((node) =>
          node.id === nodeId ? { ...node, parentId, meta: movedMeta } : node,
        ),
      );
      setSelectedNodeId(nodeId);
      setPendingFocusNodeId(nodeId);

      try {
        const response = await updateOrganizationTreeNode(nodeId, {
          orgId: orgId ?? undefined,
          parentId,
          meta: movedMeta,
        });
        applyUpsertNode(response.node);
        if (options?.trackForUndo !== false) {
          setLastMoveSnapshot({
            nodeId,
            fromParentId: previousParentId,
            toParentId: parentId,
          });
        }
      } catch (error) {
        setSyncError(toErrorMessage(error));
        setTreeNodes((previous) =>
          previous.map((node) =>
            node.id === nodeId ? { ...node, parentId: previousParentId, meta: movingNode.meta } : node,
          ),
        );
      } finally {
        setIsMutating(false);
      }
    },
    [
      applyUpsertNode,
      canManageNodeId,
      createAuditEntry,
      isValidParentForNode,
      orgId,
      treeNodes,
    ],
  );

  const nodes = useMemo(
    () =>
      baseNodes.map((node) => {
        const locked = nodeLocked(node.data.node);
        const canManage = canManageNodeId(node.id);
        return {
          ...node,
          draggable: node.data.node.parentId !== null && !locked && canManage,
          data: {
            ...node.data,
            isSelected: node.id === selectedNodeId,
            isAncestor: ancestorSet.has(node.id),
            isDropTarget: dropTargetNodeId === node.id,
            isDragging: draggingNodeId === node.id,
            isLocked: locked,
            canAddChild: canManage && !locked && NODE_CHILD_TYPE_MAP[node.data.node.type] !== null,
            canDelete: canManage && !locked && node.data.node.parentId !== null,
            onAddChild: (nodeId: string) => {
              openBranchModal(nodeId);
            },
            onDelete: (nodeId: string) => {
              openDeleteModal(nodeId);
            },
          },
        };
      }),
    [
      ancestorSet,
      baseNodes,
      canManageNodeId,
      dropTargetNodeId,
      draggingNodeId,
      openDeleteModal,
      openBranchModal,
      selectedNodeId,
    ],
  );

  const edges = useMemo(() => {
    return baseEdges.map((edge) => {
      const highlighted = ancestorEdgeSet.has(`${edge.source}=>${edge.target}`);
      const style = highlighted
        ? {
            stroke: 'rgba(224, 161, 94, 0.95)',
            strokeWidth: 3.1,
            filter: 'drop-shadow(0 0 5px rgba(224,161,94,0.65))',
          }
        : {
            stroke: 'rgba(158, 176, 222, 0.58)',
            strokeWidth: 1.7,
          };

      return {
        ...edge,
        animated: highlighted,
        style,
      } as Edge;
    });
  }, [ancestorEdgeSet, baseEdges]);

  const selectedNode = useMemo(
    () => (selectedNodeId ? treeNodes.find((node) => node.id === selectedNodeId) ?? null : null),
    [selectedNodeId, treeNodes],
  );

  const focusNode = useCallback(
    (nodeId: string, zoom = 1.15) => {
      const flow = flowRef.current;
      if (!flow) return;
      const node = nodes.find((item) => item.id === nodeId);
      if (!node) return;
      flow.setCenter(node.position.x + 115, node.position.y + 46, {
        zoom,
        duration: 350,
      });
    },
    [nodes],
  );

  useEffect(() => {
    if (!flowRef.current || !nodes.length) return;
    flowRef.current.fitView({
      duration: 350,
      padding: 0.2,
      minZoom: 0.2,
      maxZoom: 1.25,
    });
  }, [nodes]);

  useEffect(() => {
    if (!pendingFocusNodeId) return;
    const exists = nodes.some((node) => node.id === pendingFocusNodeId);
    if (!exists) return;
    focusNode(pendingFocusNodeId);
    setPendingFocusNodeId(null);
  }, [focusNode, nodes, pendingFocusNodeId]);

  const fitView = () => {
    flowRef.current?.fitView({
      duration: 350,
      padding: 0.2,
      minZoom: 0.2,
      maxZoom: 1.25,
    });
  };

  const undoLastMove = useCallback(() => {
    if (!lastMoveSnapshot) return;
    const targetParent = treeNodes.find((node) => node.id === lastMoveSnapshot.fromParentId);
    if (!targetParent || nodeLocked(targetParent)) {
      setSyncError('Cannot undo move: original parent is unavailable or locked.');
      return;
    }
    void moveNodeToParent(lastMoveSnapshot.nodeId, lastMoveSnapshot.fromParentId, {
      trackForUndo: false,
    });
    setLastMoveSnapshot(null);
  }, [lastMoveSnapshot, moveNodeToParent, treeNodes]);

  const updateNodeTitle = async (id: string, title: string) => {
    const currentNode = treeNodes.find((node) => node.id === id);
    if (!currentNode) return;
    if (!canManageNodeId(id)) {
      setSyncError('You can only modify your assigned node and its child nodes.');
      return;
    }

    const trimmed = title.trim() || currentNode.title;
    if (trimmed === currentNode.title) return;
    const updatedMeta = withAuditEntry(
      currentNode.meta,
      createAuditEntry('details_updated', `Renamed node to ${trimmed}.`),
    );

    setSyncError(null);
    setTreeNodes((previous) =>
      previous.map((node) =>
        node.id === id ? { ...node, title: trimmed, meta: updatedMeta } : node,
      ),
    );

    try {
      const response = await updateOrganizationTreeNode(id, {
        orgId: orgId ?? undefined,
        title: trimmed,
        meta: updatedMeta,
      });
      applyUpsertNode(response.node);
    } catch (error) {
      setSyncError(toErrorMessage(error));
      setTreeNodes((previous) =>
        previous.map((node) =>
          node.id === id ? { ...node, title: currentNode.title, meta: currentNode.meta } : node,
        ),
      );
    }
  };

  const updateNodeMeta = async (
    id: string,
    meta: EditableNodeMeta,
  ) => {
    const currentNode = treeNodes.find((node) => node.id === id);
    if (!currentNode) return;
    if (!canManageNodeId(id)) {
      setSyncError('You can only modify your assigned node and its child nodes.');
      return;
    }

    const mergedMeta: SkillsTreeNodeMeta = {
      ...(currentNode.meta ?? {}),
      position: meta.position.trim(),
      team: meta.team.trim(),
      designation: singularizeDesignation(meta.designation),
      childDesignation: parseDesignationOptions(meta.childDesignation).join(', '),
      appointedAgent: meta.appointedAgent.trim(),
      appointedAgentUid: meta.appointedAgentUid.trim(),
      appointedAgentEmail: meta.appointedAgentEmail.trim(),
      appointedAgentAvatarUrl: meta.appointedAgentAvatarUrl.trim(),
    };
    const auditedMeta = withAuditEntry(
      mergedMeta,
      createAuditEntry('details_updated', 'Updated node details.'),
    );

    setSyncError(null);
    setTreeNodes((previous) =>
      previous.map((node) => (node.id === id ? { ...node, meta: auditedMeta } : node)),
    );

    try {
      const response = await updateOrganizationTreeNode(id, {
        orgId: orgId ?? undefined,
        meta: auditedMeta,
      });
      applyUpsertNode(response.node);

      const designationCandidates = parseDesignationOptions(auditedMeta.childDesignation);
      if (designationCandidates.length) {
        const directChildren = treeNodes.filter((node) => node.parentId === id);
        const childrenToPatch = directChildren
          .map((child, index) => {
            const preferredDesignation =
              designationCandidates[Math.min(index, designationCandidates.length - 1)];
            const currentDesignation =
              child.meta && typeof child.meta === 'object'
                ? String((child.meta as Record<string, unknown>).designation ?? '').trim()
                : '';
            const shouldFixTitle = isLegacyPlaceholderTitle(child.title);
            const needsDesignation = !currentDesignation;
            if (!shouldFixTitle && !needsDesignation) return null;
            return {
              child,
              nextTitle: shouldFixTitle ? preferredDesignation : child.title,
              nextDesignation: needsDesignation ? preferredDesignation : currentDesignation,
            };
          })
          .filter(Boolean) as Array<{
          child: SkillsTreeNode;
          nextTitle: string;
          nextDesignation: string;
        }>;

        if (childrenToPatch.length) {
          setTreeNodes((previous) =>
            previous.map((node) => {
              const next = childrenToPatch.find((entry) => entry.child.id === node.id);
              if (!next) return node;
              const nextMeta = {
                ...(node.meta ?? {}),
                designation: next.nextDesignation,
              };
              return {
                ...node,
                title: next.nextTitle,
                meta: nextMeta,
              };
            }),
          );

          await Promise.all(
            childrenToPatch.map(({ child, nextTitle, nextDesignation }) =>
              updateOrganizationTreeNode(child.id, {
                orgId: orgId ?? undefined,
                title: nextTitle,
                meta: {
                  ...(child.meta ?? {}),
                  designation: nextDesignation,
                },
              }),
            ),
          );
        }
      }
    } catch (error) {
      setSyncError(toErrorMessage(error));
      setTreeNodes((previous) =>
        previous.map((node) => (node.id === id ? currentNode : node)),
      );
    }
  };

  const updateNodeRules = async (
    id: string,
    rules: Record<string, unknown>,
  ) => {
    const currentNode = treeNodes.find((node) => node.id === id);
    if (!currentNode) return;
    if (!canManageNodeId(id)) {
      setSyncError('You can only modify your assigned node and its child nodes.');
      return;
    }

    const nextRules = { ...rules };
    const currentRules = currentNode.rules ?? {};
    const currentSerialized = JSON.stringify(currentRules);
    const nextSerialized = JSON.stringify(nextRules);
    if (currentSerialized === nextSerialized) return;

    const auditedMeta = withAuditEntry(
      currentNode.meta,
      createAuditEntry('rules_updated', 'Updated node routing rules.'),
    );

    setSyncError(null);
    setTreeNodes((previous) =>
      previous.map((node) =>
        node.id === id ? { ...node, rules: nextRules, meta: auditedMeta } : node,
      ),
    );

    try {
      const response = await updateOrganizationTreeNode(id, {
        orgId: orgId ?? undefined,
        rules: nextRules,
        meta: auditedMeta,
      });
      applyUpsertNode(response.node);
    } catch (error) {
      setSyncError(toErrorMessage(error));
      setTreeNodes((previous) =>
        previous.map((node) => (node.id === id ? currentNode : node)),
      );
    }
  };

  return (
    <Wrap>
      <CanvasShell>
        <CanvasHeader>
          <CanvasHeaderTop>
            <CanvasTitle>Organization Brain Tree</CanvasTitle>
            <SyncBadge $tone={syncError ? 'error' : isLoading || isMutating ? 'busy' : 'ready'}>
              {syncError ? 'Sync error' : isLoading || isMutating ? 'Syncing' : 'Synced'}
            </SyncBadge>
          </CanvasHeaderTop>
          <CanvasSubtitle>
            Heirarchy, Rules, SLA
          </CanvasSubtitle>
          {syncError ? (
            <ErrorBanner>
              <AlertTriangle size={12} />
              {syncError}
              <RetryButton
                type="button"
                onClick={() => {
                  void loadTree();
                }}
              >
                Retry
              </RetryButton>
            </ErrorBanner>
          ) : null}
          {!syncError && (treeAudit?.summary.invalidRoutedNodes ?? 0) > 0 ? (
            <AuditBanner>
              <AlertTriangle size={12} />
              <span>
                {treeAudit?.summary.invalidRoutedNodes} routed Brain node
                {treeAudit?.summary.invalidRoutedNodes === 1 ? '' : 's'} are missing Team labels, so
                runtime routing ignores them.
                {invalidRoutingPreview ? ` Fix in Brain: ${invalidRoutingPreview}.` : ''}
              </span>
            </AuditBanner>
          ) : null}
        </CanvasHeader>

        <ControlRail>
          <ControlButton
            type="button"
            onClick={fitView}
            aria-label="Fit View"
            title="Fit View"
            disabled={isLoading}
          >
            <LocateFixed size={15} />
          </ControlButton>
          <ControlButton
            type="button"
            onClick={undoLastMove}
            aria-label="Undo Last Move"
            title="Undo Last Move"
            disabled={!lastMoveSnapshot || isMutating}
          >
            <Undo2 size={15} />
          </ControlButton>
          <ControlButton
            type="button"
            onClick={() => {
              void loadTree();
              void loadOperators();
            }}
            aria-label="Refresh Tree"
            title="Refresh Tree"
            disabled={isLoading || isMutating}
          >
            <RefreshCw size={15} />
          </ControlButton>
        </ControlRail>

        <ReactFlow<SkillsFlowNode, Edge>
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2, minZoom: 0.2, maxZoom: 1.25 }}
          minZoom={0.2}
          maxZoom={1.6}
          nodesDraggable
          nodesConnectable={false}
          elementsSelectable
          panOnDrag
          zoomOnScroll
          zoomOnPinch
          zoomOnDoubleClick={false}
          proOptions={{ hideAttribution: true }}
          onInit={(instance) => {
            flowRef.current = instance;
          }}
          onNodeDragStart={(_, node) => {
            const source = treeNodes.find((candidate) => candidate.id === node.id);
            if (nodeLocked(source)) return;
            setDraggingNodeId(node.id);
            setDropTargetNodeId(null);
          }}
          onNodeDrag={(_, node) => {
            const nextDropTargetId = detectDropTargetId({
              id: node.id,
              position: node.position,
              width: node.width,
              height: node.height,
            });
            setDropTargetNodeId(nextDropTargetId);
          }}
          onNodeDragStop={(_, node) => {
            const nextDropTargetId = detectDropTargetId({
              id: node.id,
              position: node.position,
              width: node.width,
              height: node.height,
            });
            if (nextDropTargetId && isValidParentForNode(node.id, nextDropTargetId)) {
              void moveNodeToParent(node.id, nextDropTargetId);
            }
            setDraggingNodeId(null);
            setDropTargetNodeId(null);
          }}
          onNodeClick={(_, node) => {
            setSelectedNodeId(node.id);
            focusNode(node.id);
          }}
          onPaneClick={() => {
            if (!draggingNodeId) setSelectedNodeId(null);
          }}
        >
          <Background gap={18} size={1} color="rgba(255,255,255,0.12)" />
        </ReactFlow>

        {!isLoading && !treeNodes.length ? (
          <EmptyState>
            <EmptyPill>No nodes found for this organization yet.</EmptyPill>
          </EmptyState>
        ) : null}
      </CanvasShell>
      <BranchModalBackdrop
        type="button"
        aria-label="Close modal"
        $open={Boolean(branchDraft) || Boolean(deleteDraft)}
        onClick={() => {
          if (branchDraft) {
            closeBranchModal();
            return;
          }
          if (deleteDraft) {
            closeDeleteModal();
          }
        }}
      />
      <BranchModal
        role="dialog"
        aria-modal="true"
        aria-label="Create child node"
        $open={Boolean(branchDraft)}
      >
        <BranchModalTitle>Create child node</BranchModalTitle>
        <BranchModalSub>
          {branchDraft
            ? `Parent node: ${branchDraft.parentTitle}`
            : 'Provide the child designation to create this node.'}
        </BranchModalSub>
        {branchDraft?.designationOptions.length ? (
          <BranchField>
            Designation
            <BranchSelect
              value={branchDraft.selectedDesignation}
              onChange={(event) =>
                setBranchDraft((previous) =>
                  previous
                    ? {
                        ...previous,
                        selectedDesignation: event.target.value,
                      }
                    : previous,
                )
              }
            >
              <option value="">Select designation</option>
              {branchDraft.designationOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </BranchSelect>
          </BranchField>
        ) : (
          <BranchField>
            Designation
            <BranchInput
              value={branchDraft?.customDesignation ?? ''}
              placeholder="Enter designation"
              onChange={(event) =>
                setBranchDraft((previous) =>
                  previous
                    ? {
                        ...previous,
                        customDesignation: event.target.value,
                      }
                    : previous,
                )
              }
            />
          </BranchField>
        )}
        <BranchActions>
          <BranchActionButton type="button" $tone="neutral" onClick={closeBranchModal}>
            Cancel
          </BranchActionButton>
          <BranchActionButton
            type="button"
            $tone="primary"
            disabled={isMutating}
            onClick={() => {
              void createChildFromBranchDraft();
            }}
          >
            Create node
          </BranchActionButton>
        </BranchActions>
      </BranchModal>
      <DeleteModal
        role="dialog"
        aria-modal="true"
        aria-label="Delete node"
        $open={Boolean(deleteDraft)}
      >
        <DeleteModalTitle>Delete node</DeleteModalTitle>
        <DeleteModalMessage>
          {deleteDraft
            ? deleteDraft.descendants
              ? `Delete "${deleteDraft.title}" and ${deleteDraft.descendants} child node${deleteDraft.descendants === 1 ? '' : 's'}?`
              : `Delete "${deleteDraft.title}"?`
            : 'Confirm node deletion.'}
        </DeleteModalMessage>
        <BranchActions>
          <DeleteActionButton type="button" $tone="neutral" onClick={closeDeleteModal}>
            Cancel
          </DeleteActionButton>
          <DeleteActionButton
            type="button"
            $tone="danger"
            disabled={!deleteDraft || isMutating}
            onClick={() => {
              if (!deleteDraft) return;
              void deleteBranch(deleteDraft.nodeId);
            }}
          >
            Delete
          </DeleteActionButton>
        </BranchActions>
      </DeleteModal>
      <NodeDetailsDrawer
        node={selectedNode}
        allNodes={treeNodes}
        open={Boolean(selectedNode)}
        operatorOptions={operatorOptions}
        serviceRoutingOptions={serviceRoutingOptions}
        serviceRoutingOptionsLoading={isLoadingRoutingOptions}
        operatorsLoading={isLoadingOperators}
        canEdit={selectedNode ? canManageNodeId(selectedNode.id) : false}
        onClose={() => setSelectedNodeId(null)}
        onTitleChange={(id, title) => {
          void updateNodeTitle(id, title);
        }}
        onMetaChange={(id, meta) => {
          void updateNodeMeta(id, meta);
        }}
        onRulesChange={(id, rules) => {
          void updateNodeRules(id, rules);
        }}
      />
    </Wrap>
  );
}

export default function SkillsTree() {
  return (
    <ReactFlowProvider>
      <SkillsTreeCanvas />
    </ReactFlowProvider>
  );
}
